import FetchAnalytics, { Dashboard } from "@/api/dashboard/FetchAnalytics";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import { useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { Link } from "react-router-dom";
import profileIcon from "../assets/profile.png";
import { useSelector } from "react-redux";
import { useState } from "react";
import { RootState } from "@/store/store";
// import hasPermission from "@/lib/hasPermission";
// import { modules, permissions } from "@/lib/permissions";
// import Welcome from "@/components/dashboard/Welcome";
import MarginChart from "@/components/dashboard/MarginChart";
import { BODY_OUTER } from "@/v2/layout/AdminLayout";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import { Role } from "@/v2/types";

function ManpowerCard(props: { className: string; data: Dashboard }) {
    return (
        <Card className={cn("flex flex-col", props.className)}>
            <CardHeader>
                <CardTitle>MANPOWER EXPIRY</CardTitle>
                <CardDescription>
                    Keep track of expiring Iqama ID's
                </CardDescription>
            </CardHeader>

            <CardContent className="grid grid-cols-3">
                <div className="flex flex-grow flex-col items-center">
                    <div className="font-semibold md:text-xl lg:text-2xl xl:text-4xl">
                        {props.data.expired_employees_count} ID's
                    </div>
                    <div className="mt-2 text-sm">EXPIRED</div>
                </div>
                <div className="flex flex-grow flex-col items-center">
                    <div className="font-semibold md:text-xl lg:text-2xl xl:text-4xl">
                        {props.data.expired_employees_count_in_this_week} ID's
                    </div>
                    <div className="mt-2 text-sm">EXPIRES NEXT WEEK</div>
                </div>
                <div className="flex flex-grow flex-col items-center">
                    <div className="font-semibold md:text-xl lg:text-2xl xl:text-4xl">
                        {props.data.employees_missing_id_expiry.length} ID's
                    </div>
                    <div className="mt-2 text-sm">MISSING </div>
                </div>
            </CardContent>

            <CardFooter className="flex justify-between">
                <div className="pr-4 text-sm text-muted-foreground">
                    {props.data.percent_employees_expire_in_this_week.toFixed(
                        0,
                    )}
                    % Of the ID's Expires within a week
                </div>
                <Link to={"/employee-database/"}>
                    <Button>Explore</Button>
                </Link>
            </CardFooter>
        </Card>
    );
}

function ManpowerPlantIdCard(props: { className: string; data: Dashboard }) {
    return (
        <Card className={cn("flex flex-col", props.className)}>
            <CardHeader>
                <CardTitle>MANPOWER PLANT ID EXPIRY</CardTitle>
                <CardDescription>
                    Keep track of expiring Plant ID's
                </CardDescription>
            </CardHeader>

            <CardContent className="grid grid-cols-3">
                <div className="flex flex-grow flex-col items-center">
                    <div className="font-semibold md:text-xl lg:text-2xl xl:text-4xl">
                        {props.data.plant_id_expiring_soon_count} ID's
                    </div>
                    <div className="mt-2 text-sm">EXPIRED</div>
                </div>
                <div className="flex flex-grow flex-col items-center">
                    <div className="font-semibold md:text-xl lg:text-2xl xl:text-4xl">
                        {props.data.plant_id_expiring_soon_count} ID's
                    </div>
                    <div className="mt-2 text-sm">EXPIRES NEXT WEEK</div>
                </div>
            </CardContent>

            <CardFooter className="flex justify-between">
                <div className="pr-4 text-sm text-muted-foreground">
                    {props.data.percent_employees_expire_in_this_week.toFixed(
                        0,
                    )}
                    % Of the ID's Expires within a week
                </div>
                <Link to={"/employee-database/"}>
                    <Button>Explore</Button>
                </Link>
            </CardFooter>
        </Card>
    );
}

function ARManpowerCard(props: { className: string; data: Dashboard }) {
    return (
        <Card className={cn("flex flex-col", props.className)}>
            <CardHeader>
                <CardTitle> EMPLOYEES EXPIRY</CardTitle>
                <CardDescription>
                    Keep track of expiring Iqama ID's
                </CardDescription>
            </CardHeader>

            <CardContent className="grid grid-cols-2">
                <div className="flex flex-grow flex-col items-center">
                    <div className="font-semibold md:text-xl lg:text-2xl xl:text-4xl">
                        {props.data.ar_employees_id_expiry.length} ID's
                    </div>
                    <div className="mt-2 text-sm">EXPIRED</div>
                </div>
                <div className="flex flex-grow flex-col items-center">
                    <div className="font-semibold md:text-xl lg:text-2xl xl:text-4xl">
                        {props.data.ar_employees_id_expiry_next_week.length}{" "}
                        ID's
                    </div>
                    <div className="mt-2 text-sm">EXPIRES NEXT WEEK</div>
                </div>
            </CardContent>

            <CardFooter className="flex justify-between">
                <div className="pr-4 text-sm text-muted-foreground">
                    {props.data.percentage_ar_employees_expire_in_this_week.toFixed(
                        0,
                    )}
                    % Of the Employee ID's Expires within a week
                </div>
                <Link to={"/employee-database/"}>
                    <Button>Explore</Button>
                </Link>
            </CardFooter>
        </Card>
    );
}

function PayableInvoiceAmount(props: { className: string; data: Dashboard }) {
    return (
        <Card className={cn("flex flex-col", props.className)}>
            <CardHeader>
                <CardTitle>TOTAL PAYABLE AMOUNT </CardTitle>
                <CardDescription>
                    Total amount to pay according to invoice
                </CardDescription>
            </CardHeader>

            <CardContent className="flex flex-grow items-center">
                <div className="flex flex-grow flex-col items-center justify-center">
                    <div className="text-4xl font-semibold">
                        {props.data.payable_invoice_total_due_amount.toFixed(2)}
                        SR
                    </div>
                </div>
            </CardContent>

            <CardFooter className="flex justify-end">
                <Link to={"/invoice-tracker/payable-invoice"}>
                    <Button>Explore</Button>
                </Link>
            </CardFooter>
        </Card>
    );
}

function ReceivableInvoiceAmount(props: {
    className: string;
    data: Dashboard;
}) {
    return (
        <Card className={cn("flex flex-col", props.className)}>
            <CardHeader>
                <CardTitle>TOTAL RECEIVABLE AMOUNT</CardTitle>
                <CardDescription>
                    Total amount to receive according to invoice
                </CardDescription>
            </CardHeader>

            <CardContent className="flex flex-grow items-center">
                <div className="flex flex-grow flex-col items-center justify-center">
                    <div className="text-4xl font-semibold">
                        {props.data.receivable_invoice_total_due_amount.toFixed(
                            2,
                        )}
                        SR
                    </div>
                </div>
            </CardContent>

            <CardFooter className="flex justify-end">
                <Link to={"/invoice-tracker/receivable-invoice"}>
                    <Button>Explore</Button>
                </Link>
            </CardFooter>
        </Card>
    );
}

function ProfitCard(props: {
    className: string;
    data: Dashboard;
    selectedYear: number;
    setSelectedYear: React.Dispatch<React.SetStateAction<number>>;
}) {
    return (
        <Card className={cn("flex flex-col justify-between", props.className)}>
            <CardHeader>
                <CardTitle>MARGIN TRACKER</CardTitle>
                <CardDescription>
                    Margin from current activate mobilization
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                <div className="text-center text-lg font-bold">
                    Current Margin : {props.data.total_margin.toFixed(2)} SR
                </div>
                <div className="flex w-full flex-grow flex-col justify-between">
                    <MarginChart
                        data={props.data.margins}
                        year={props.selectedYear}
                    />
                    {props.selectedYear === new Date().getFullYear() ? (
                        <Button
                            onClick={() => {
                                props.setSelectedYear(
                                    new Date().getFullYear() - 1,
                                );
                            }}
                            className="mt-4"
                            variant="outline"
                        >
                            View {props.selectedYear - 1} Data
                        </Button>
                    ) : (
                        <Button
                            onClick={() => {
                                props.setSelectedYear(new Date().getFullYear());
                            }}
                            className="mt-4"
                            variant="outline"
                        >
                            View {props.selectedYear + 1} Data
                        </Button>
                    )}
                </div>
            </CardContent>
            <CardFooter className="flex justify-between">
                <div className="text-sm text-muted-foreground"></div>
            </CardFooter>
        </Card>
    );
}

function ExpiredIqama(props: { className: string; data: Dashboard }) {
    return (
        <Card className={cn("flex flex-col justify-between", props.className)}>
            <div>
                <CardHeader>
                    <CardTitle>EXPIRED EMPLOYEES IQAMA ID'S</CardTitle>
                    <CardDescription>
                        Keep track of employees with expired ID's
                    </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-8">
                    {props.data.ar_employees_id_expiry.length == 0 && (
                        <p className="flex items-center justify-center text-center text-2xl font-semibold text-muted-foreground">
                            No Data
                        </p>
                    )}
                    {props.data.ar_employees_id_expiry
                        .slice(0, 6)
                        .map((employee) => {
                            return (
                                <div
                                    key={employee.id}
                                    className="flex items-center justify-between gap-4"
                                >
                                    <div className="flex items-center gap-4">
                                        <Avatar className="hidden h-10 w-10 sm:flex">
                                            <AvatarImage
                                                src={profileIcon}
                                                alt="Avatar"
                                            />
                                            <AvatarFallback></AvatarFallback>
                                        </Avatar>
                                        <div className="grid gap-1">
                                            <p className="text-sm font-medium leading-none">
                                                {employee.full_name}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {employee.trade}
                                            </p>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/employee-database/${employee.id}`}
                                    >
                                        <Button variant={"outline"}>
                                            <Eye className="size-4" />
                                        </Button>
                                    </Link>
                                </div>
                            );
                        })}
                </CardContent>
            </div>
            <CardFooter className="flex flex-row-reverse justify-between text-muted-foreground">
                <Link to={"/employee-database/category/ar-employees"}>
                    <Button>View All</Button>
                </Link>
                {props.data.ar_employees_id_expiry.length - 6 > 0 && (
                    <p className="text-sm text-muted-foreground">
                        {props.data.ar_employees_id_expiry.length - 6} more ID's
                        have been expired
                    </p>
                )}
            </CardFooter>
        </Card>
    );
}

function OverDuePayableInvoice(props: { className: string; data: Dashboard }) {
    return (
        <Card className={cn("flex flex-col justify-between", props.className)}>
            <div>
                <CardHeader>
                    <CardTitle>Over Due Payable Invoice</CardTitle>
                    <CardDescription>
                        Keep track of over due payable invoices
                    </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-8">
                    {props.data.payable_invoice_over_due.length == 0 && (
                        <p className="flex items-center justify-center text-center text-2xl font-semibold text-muted-foreground">
                            No Data
                        </p>
                    )}
                    {props.data.payable_invoice_over_due
                        .slice(0, 4)
                        .map((invoice) => {
                            return (
                                <div
                                    key={invoice.id}
                                    className="flex items-center justify-between gap-4"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="grid gap-1">
                                            <p className="text-sm font-medium leading-none">
                                                Invoice Number :{" "}
                                                {invoice.invoice_number}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                Invoice Amount Including VAT :{" "}
                                                {
                                                    invoice.invoice_amount_including_vat
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/invoice-tracker/payable-invoice/${invoice.id}`}
                                    >
                                        <Button variant={"outline"}>
                                            <Eye className="size-4" />
                                        </Button>
                                    </Link>
                                </div>
                            );
                        })}
                </CardContent>
            </div>
            <CardFooter className="flex flex-row-reverse justify-between text-muted-foreground">
                <Link to={"/invoice-tracker/payable-invoice"}>
                    <Button>View All</Button>
                </Link>
                {props.data.payable_invoice_over_due.length - 4 > 0 && (
                    <p className="text-sm text-muted-foreground">
                        {props.data.payable_invoice_over_due.length - 4} more
                        invoices's are due.
                    </p>
                )}
            </CardFooter>
        </Card>
    );
}

function OverDueReceivableInvoice(props: {
    className: string;
    data: Dashboard;
}) {
    return (
        <Card className={cn("flex flex-col justify-between", props.className)}>
            <div>
                <CardHeader>
                    <CardTitle>Over Due Receivable Invoice</CardTitle>
                    <CardDescription>
                        Keep track of over due receivable invoices
                    </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-8">
                    {props.data.receivable_invoice_over_due.length == 0 && (
                        <p className="flex items-center justify-center text-center text-2xl font-semibold text-muted-foreground">
                            No Data
                        </p>
                    )}
                    {props.data.receivable_invoice_over_due
                        .slice(0, 4)
                        .map((invoice) => {
                            return (
                                <div
                                    key={invoice.id}
                                    className="flex items-center justify-between gap-4"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="grid gap-1">
                                            <p className="text-sm font-medium leading-none">
                                                Invoice Number :{" "}
                                                {invoice.invoice_number}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                Invoice Amount Including VAT :{" "}
                                                {
                                                    invoice.invoice_amount_including_vat
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/invoice-tracker/receivable-invoice/${invoice.id}`}
                                    >
                                        <Button variant={"outline"}>
                                            <Eye className="size-4" />
                                        </Button>
                                    </Link>
                                </div>
                            );
                        })}
                </CardContent>
            </div>
            <CardFooter className="flex flex-row-reverse justify-between text-muted-foreground">
                <Link to={"/invoice-tracker/receivable-invoice"}>
                    <Button>View All</Button>
                </Link>
                {props.data.receivable_invoice_over_due.length - 4 > 0 && (
                    <p className="text-sm text-muted-foreground">
                        {props.data.receivable_invoice_over_due.length - 4} more
                        invoices's are due.
                    </p>
                )}
            </CardFooter>
        </Card>
    );
}

const DashboardPage = () => {
    // State and Hooks
    const user = useSelector((state: RootState) => state.user.value);
    const [selectedYear, setSelectedYear] = useState<number>(
        new Date().getFullYear(),
    );

    // User Role Checks
    const isManagingDirector = user?.roles?.some(
        (role: Role) =>
            role.handle === "MANAGING_DIRECTOR" ||
            role.handle === "HR_ADMINISTRATION_OFFICER",
    );
    const isBDM = user?.roles?.some(
        (role: Role) => role.handle === "BUSINESS_DEVELOPMENT_MANAGER",
    );

    const isBDC = user?.roles?.some(
        (role: Role) => role.handle === "BUSINESS_COORDINATORS",
    );

    const isAdministrator = user?.roles?.some(
        (role: Role) => role.handle === "ADMINISTRATION_OFFICER",
    );

    const isSuperAdmin = user?.is_super_admin;

    // if (
    //     !hasPermission(user, {
    //         module: modules.ANALYTICS_MANAGEMENT,
    //         scope: permissions.READ_ANALYTICS_MANAGEMENT,
    //     })
    // ) {
    //     return <Welcome></Welcome>;
    // }
    // Data Fetching

    const { data, isLoading, error } = useQuery({
        queryKey: ["analytics", selectedYear],
        queryFn: () => FetchAnalytics(selectedYear),
        enabled: true,
    });
    // Loading States
    if (isLoading) return <LoadingAnimation />;
    if (!data) return <p>No Data</p>;
    if (error) return <div>Error in Loading analytics...</div>;

    // Event Handlers

    // Main Dashboard Layout
    return (
        <div
            style={{
                height: `calc(100vh - ${BODY_OUTER + 20}px)`,
            }}
            className="flex flex-wrap gap-4 overflow-y-scroll"
        >
            {/* Right Column */}
            {(isManagingDirector || isSuperAdmin) && (
                <div className="grid w-full grid-cols-3 gap-4">
                    <ProfitCard
                        data={data}
                        className="col-span-2 h-full w-full"
                        selectedYear={selectedYear}
                        setSelectedYear={setSelectedYear}
                    />
                    <div className="grid grid-cols-1 gap-4">
                        <ReceivableInvoiceAmount className="" data={data} />
                        <PayableInvoiceAmount className="" data={data} />
                    </div>
                </div>
            )}

            {/* Left Column */}
            <div
                className={`grid w-full gap-10 ${isBDC ? "grid-cols-1" : "grid-cols-2"}`}
            >
                <div className={`grid grid-cols-1 gap-4`}>
                    <ManpowerCard className="" data={data} />
                    <ManpowerPlantIdCard className="" data={data} />
                    {!isBDC && <ARManpowerCard className="" data={data} />}
                </div>
                <div className="flex w-full gap-4">
                    {!isBDC && <ExpiredIqama className="w-full" data={data} />}
                </div>
            </div>

            {(isBDM ||
                isAdministrator ||
                isManagingDirector ||
                isSuperAdmin) && (
                <div className="grid w-full grid-cols-2 flex-wrap justify-center gap-10">
                    {!isBDM && (
                        <OverDuePayableInvoice className="w-full" data={data} />
                    )}
                    <OverDueReceivableInvoice className="w-full" data={data} />
                </div>
            )}
        </div>
    );
};
export default DashboardPage;
